import { stopConversationSubAgents } from "./stopConversationSubAgents";
import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { type Context, userFacingError } from "../authorization";
import { findUniqueName } from "../findUniqueName";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { clientGeneratableIdSchema } from "../id";
import { createBranch } from "./createBranch";
import { sendPersonalChatMessageSchema } from "./sendPersonalChatMessage";
import { assertAttachmentsSupported } from "./attachmentValidation";
import { mutator } from "./mutator";
import { insertUserInterruptEntry, nextRespondingAt } from "./utils";

export async function requireLiveChat(
  tx: Transaction<Schema>,
  ctx: Context,
  workflowId: string,
  chatId: string
) {
  const row = await tx.run(
    zql.workflowChats
      .where("chatId", chatId)
      .where("workflowId", workflowId)
      .where("userId", ctx.userId)
      .related("chat")
      .one()
  );
  if (!row?.chat || row.chat.commitId != null)
    throw userFacingError("Live chat not found");
  return row.chat;
}

export const sendLiveChatMessageSchema = sendPersonalChatMessageSchema.extend({
  workflowId: z.string(),
  chatId: clientGeneratableIdSchema,
  actionKind: z.enum(["sub_agent"]).optional(),
});

export const sendLiveChatMessage = mutator({
  description: "Asks a private question about a workflow’s live version.",
  interactionType: "send",
  redactKeys: ["text"],
  schema: sendLiveChatMessageSchema,
  resolve: { workflow: { kind: "workflow", from: "workflowId" } },
  permissions: [
    { permission: "space:read", scopeId: ({ args }) => args.workflow.spaceId },
  ],
  fn: async ({ tx, ctx, args }) => {
    const { workflow, chatId, entryId, text, attachments, interruptEntryId } =
      args;
    if (!text.trim() && !attachments?.length)
      throw userFacingError("Message must have text or attachments");
    await assertAttachmentsSupported(tx, attachments, args.chatModel);
    const live = await tx.run(
      zql.workflowBranches
        .where("workflowId", workflow.id)
        .where("name", "main")
        .one()
    );
    if (!live?.headCommitId) throw userFacingError("Live version not found");
    const existing = await tx.run(
      zql.workflowChats
        .where("workflowId", workflow.id)
        .where("userId", ctx.userId)
        .one()
    );
    const now = Date.now();
    if (!existing) {
      if (await tx.run(zql.chats.where("id", chatId).one()))
        throw userFacingError("Chat ID is already in use");
      await tx.mutate.chats.insert({
        id: chatId,
        commitId: null,
        createdAt: now,
        lastChatEntryType: null,
        respondingAt: null,
        lastLoopHeartbeatAt: null,
        awaitingAction: false,
        tenantId: ctx.tenantId,
      });
      await tx.mutate.workflowChats.insert({
        chatId,
        workflowId: workflow.id,
        userId: ctx.userId,
        tenantId: ctx.tenantId,
      });
    }
    const chat = await requireLiveChat(
      tx,
      ctx,
      workflow.id,
      existing?.chatId ?? chatId
    );
    if (chat.respondingAt && !interruptEntryId)
      throw userFacingError("Wait for the current reply to finish");
    const last = await tx.run(
      zql.chatEntries
        .where("chatId", chat.id)
        .orderBy("createdAt", "desc")
        .orderBy("id", "desc")
        .one()
    );
    const createdAt = Math.max(now, (last?.createdAt ?? 0) + 1);
    if (interruptEntryId)
      await insertUserInterruptEntry(tx, {
        id: interruptEntryId,
        chatId: chat.id,
        createdAt,
        previousEntryId: last?.id ?? null,
        tenantId: ctx.tenantId,
      });
    await tx.mutate.chatEntries.insert({
      id: entryId,
      chatId: chat.id,
      entryType: "UserMessage",
      createdAt: createdAt + (interruptEntryId ? 1 : 0),
      previousEntryId: interruptEntryId ?? last?.id ?? null,
      streaming: false,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.userMessages.insert({
      entryId,
      userId: ctx.userId,
      text,
      isDictated: args.isDictated ?? null,
      effort: args.effort ?? null,
      planMode: args.planMode ?? false,
      actionKind: args.actionKind ?? null,
      tenantId: ctx.tenantId,
    });
    for (const attachment of attachments ?? [])
      await tx.mutate.attachments.insert({
        ...attachment,
        entryId,
        tenantId: ctx.tenantId,
      });
    await tx.mutate.chats.update({
      id: chat.id,
      lastChatEntryType: "UserMessage",
      respondingAt: nextRespondingAt(chat.respondingAt),
      awaitingAction: false,
    });
  },
});

export const clearLiveChatSchema = z.object({
  workflowId: z.string(),
  chatId: z.string(),
});
export const clearLiveChat = mutator({
  description:
    "Clears the user’s private conversation with a workflow’s live version.",
  interactionType: "delete",
  schema: clearLiveChatSchema,
  resolve: { workflow: { kind: "workflow", from: "workflowId" } },
  permissions: [
    { permission: "space:read", scopeId: ({ args }) => args.workflow.spaceId },
  ],
  fn: async ({ tx, ctx, args }) => {
    await requireLiveChat(tx, ctx, args.workflow.id, args.chatId);
    await tx.mutate.workflowChats.delete({ chatId: args.chatId });
    await tx.mutate.chats.delete({ id: args.chatId });
  },
});

export const interruptLiveChatSchema = z.object({
  workflowId: z.string(),
  chatId: z.string(),
  entryId: clientGeneratableIdSchema,
});
export const interruptLiveChat = mutator({
  description: "Stops a reply in the user’s private live conversation.",
  interactionType: "update",
  schema: interruptLiveChatSchema,
  resolve: { workflow: { kind: "workflow", from: "workflowId" } },
  permissions: [
    { permission: "space:read", scopeId: ({ args }) => args.workflow.spaceId },
  ],
  fn: async ({ tx, ctx, args }) => {
    const chat = await requireLiveChat(tx, ctx, args.workflow.id, args.chatId);
    const last = await tx.run(
      zql.chatEntries
        .where("chatId", args.chatId)
        .orderBy("createdAt", "desc")
        .orderBy("id", "desc")
        .one()
    );
    await insertUserInterruptEntry(tx, {
      id: args.entryId,
      chatId: args.chatId,
      createdAt: Math.max(Date.now(), (last?.createdAt ?? 0) + 1),
      previousEntryId: last?.id ?? null,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.chats.update({
      id: args.chatId,
      respondingAt: null,
      awaitingAction: false,
      lastChatEntryType: "UserInterrupt",
    });
    await stopConversationSubAgents(
      tx,
      { liveChatId: args.chatId },
      chat.respondingAt
    );
  },
});

export const answerLiveChatBranchRequestSchema = z.object({
  workflowId: z.string(),
  chatId: z.string(),
  // Absent when the user continues in a branch without the assistant asking.
  entryId: z.string().optional(),
  createBranch: z.boolean(),
  branchId: clientGeneratableIdSchema,
  commitId: clientGeneratableIdSchema,
  temporaryChatId: clientGeneratableIdSchema,
  chatModel: z.string().optional(),
});
export const answerLiveChatBranchRequest = mutator({
  description:
    "Continues a private live conversation in a shared branch, or declines the assistant’s request to.",
  interactionType: "review",
  schema: answerLiveChatBranchRequestSchema,
  resolve: { workflow: { kind: "workflow", from: "workflowId" } },
  // createBranch.fn enforces write access when the user creates a branch.
  permissions: [
    { permission: "space:read", scopeId: ({ args }) => args.workflow.spaceId },
  ],
  fn: async ({ tx, ctx, args }) => {
    const { workflow, chatId, entryId, branchId, commitId } = args;
    const chat = await requireLiveChat(tx, ctx, workflow.id, chatId);
    if (chat.respondingAt)
      throw userFacingError("Wait for the current reply to finish");
    const now = Date.now();
    if (entryId) {
      const entry = await tx.run(
        zql.chatEntries
          .where("id", entryId)
          .where("chatId", chat.id)
          .related("toolCall")
          .one()
      );
      const lastRequest = await tx.run(
        zql.chatEntries
          .where("chatId", chat.id)
          .whereExists("toolCall", (t) =>
            t.where("toolName", "IN", ["requestBranch", "approvePlan"])
          )
          .orderBy("createdAt", "desc")
          .orderBy("id", "desc")
          .one()
      );
      if (
        !chat.awaitingAction ||
        lastRequest?.id !== entry?.id ||
        !entry?.toolCall ||
        !["requestBranch", "approvePlan"].includes(entry.toolCall.toolName) ||
        entry.toolCall.completedAt != null
      )
        throw userFacingError("Branch request is no longer available");
      await tx.mutate.toolCalls.update({
        entryId: entry.id,
        output: {
          ...(entry.toolCall.toolName === "approvePlan"
            ? { approved: args.createBranch }
            : { created: args.createBranch }),
          branchId: args.createBranch ? branchId : null,
        },
        isError: false,
        completedAt: now,
      });
    } else if (!args.createBranch || chat.awaitingAction) {
      throw userFacingError("Answer the pending question before continuing");
    }
    if (args.createBranch) {
      const [branch, commit, temporaryChat] = await Promise.all([
        tx.run(zql.workflowBranches.where("id", branchId).one()),
        tx.run(zql.workflowCommits.where("id", commitId).one()),
        tx.run(zql.chats.where("id", args.temporaryChatId).one()),
      ]);
      if (branch || commit || temporaryChat)
        throw userFacingError("Branch IDs are already in use");
      const existingBranches = await tx.run(
        zql.workflowBranches.where("workflowId", workflow.id)
      );
      await createBranch.fn({
        tx,
        ctx,
        args: {
          workflowId: workflow.id,
          branchId,
          commitId,
          chatId: args.temporaryChatId,
          name: findUniqueName(
            "New branch",
            new Set(existingBranches.map((b) => b.name))
          ),
        },
      });
      // createBranch always inserts a chat, so swap in the private one.
      await tx.mutate.chats.delete({ id: args.temporaryChatId });
      await tx.mutate.chats.update({ id: chatId, commitId });
      await tx.mutate.workflowCommits.update({ id: commitId, chatId });
      await tx.mutate.workflowChats.delete({ chatId });
      await tx.mutate.workflowBranches.update({
        id: branchId,
        lastChatEntryAt: now,
      });
      await tx.mutate.branchContributors.update({
        branchId,
        userId: ctx.userId,
        lastMessagedAt: now,
      });
    }
    const respondingAt =
      args.createBranch && entryId ? nextRespondingAt(chat.respondingAt) : null;
    await tx.mutate.chats.update({
      id: chat.id,
      awaitingAction: false,
      respondingAt,
    });
    const agents = await tx.run(
      zql.subAgents
        .where("liveChatId", chat.id)
        .where("status", "IN", ["running", "needs_input"])
    );
    for (const agent of agents) {
      await tx.mutate.subAgents.update({
        id: agent.id,
        branchId: args.createBranch ? branchId : null,
        liveChatId: args.createBranch ? null : chat.id,
        status: respondingAt !== null ? "running" : "idle",
        boundRespondingAt: respondingAt,
        outcome:
          respondingAt !== null
            ? null
            : {
                status: "idle",
                workflowId: workflow.id,
                ...(args.createBranch ? { branchId } : {}),
                summary: args.createBranch
                  ? "The user continued the conversation in a branch."
                  : "The user kept the conversation in planning.",
                pauseAfter: true,
              },
        deliveryPendingAt: respondingAt !== null ? null : now,
        updatedAt: now,
      });
    }
  },
});
